import { useRef } from "react";

/**
 * ScanButton — pulsante "🖨️ Importa da scanner" che apre il file picker del sistema.
 *
 * Gli scanner d'ufficio salvano i documenti scansionati come PDF o JPG in una cartella
 * locale o di rete (es. "Scansioni", "Documenti", o una share di rete). Questo pulsante
 * apre il file picker standard del sistema operativo: l'operatore può navigare nella
 * cartella di output dello scanner e selezionare il file scansionato.
 *
 * Su mobile (dove non c'è uno scanner) apre comunque il file picker: l'utente può
 * scegliere file salvati o foto già fatte.
 *
 * Props:
 *  - onFile: (file: File) => void  — callback con il file selezionato
 *  - accept: string                 — accept dei file (default "image/*,application/pdf")
 *  - label: string                  — etichetta visibile (default "Importa da scanner")
 *  - className: string              — classi aggiuntive
 *  - testid: string                 — data-testid
 *  - disabled: bool
 *  - variant: "primary" | "outline" | "ghost" (stile visivo)
 */
export const ScanButton = ({
  onFile,
  accept = "image/*,application/pdf",
  label = "Importa da scanner",
  className = "",
  testid = "scan-btn",
  disabled = false,
  variant = "outline",
}) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile?.(f);
    e.target.value = "";
  };

  const styles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white border-blue-600",
    outline: "border-blue-300 hover:bg-blue-100 text-blue-700 bg-white",
    ghost: "border-transparent hover:bg-blue-50 text-blue-700",
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant] || styles.outline} ${className}`}
        title="Apre il file picker per importare un documento scansionato dal tuo scanner (PDF/JPG salvato dallo scanner d'ufficio)"
      >
        🖨️ {label}
      </button>
    </>
  );
};

export default ScanButton;

